import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Doughnut, Bar, Line, HorizontalBar } from 'react-chartjs-2';
import { dadosGraficoCruzamento, filtrarOrdensRelatorio } from './ordemServicoRelatorioUtils';

const STATUS_ORDEM = [
  'ABERTA',
  'AGUARDANDO_MATERIAL',
  'EM_ATENDIMENTO',
  'AGUARDANDO_VALIDACAO',
  'FINALIZADA',
  'CANCELADA',
];

const STATUS_LABELS = {
  ABERTA: 'Aberta',
  AGUARDANDO_MATERIAL: 'Aguard. material',
  EM_ATENDIMENTO: 'Em atendimento',
  AGUARDANDO_VALIDACAO: 'Aguard. validação',
  FINALIZADA: 'Finalizada',
  CANCELADA: 'Cancelada',
};

const STATUS_CORES = {
  ABERTA: '#3c8dbc',
  AGUARDANDO_MATERIAL: '#f39c12',
  EM_ATENDIMENTO: '#8e44ad',
  AGUARDANDO_VALIDACAO: '#16a085',
  FINALIZADA: '#27ae60',
  CANCELADA: '#94a3b8',
};

const TIPO_LABELS = {
  CORRETIVA: 'Corretiva',
  PREVENTIVA: 'Preventiva',
  IMPLANTACAO: 'Implantação',
  RECLAMACAO: 'Reclamação',
};

const TIPO_CORES = ['#3c8dbc', '#8e44ad', '#16a085', '#f39c12'];

const PERIODOS = [
  { value: '30', label: '30 dias' },
  { value: '90', label: '90 dias' },
  { value: '180', label: '6 meses' },
  { value: 'all', label: 'Tudo' },
];

const OPCOES_BASE = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: true,
    position: 'bottom',
    labels: {
      boxWidth: 12,
      padding: 14,
      fontColor: '#64748b',
      fontSize: 11,
      usePointStyle: true,
    },
  },
  tooltips: {
    backgroundColor: 'rgba(15, 23, 42, 0.92)',
    titleFontStyle: '600',
    bodyFontSize: 12,
    cornerRadius: 8,
    xPadding: 12,
    yPadding: 10,
  },
};

const OPCOES_BARRA = {
  ...OPCOES_BASE,
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, precision: 0, fontColor: '#64748b', fontSize: 11 },
        gridLines: { color: 'rgba(148, 163, 184, 0.15)', drawBorder: false },
      },
    ],
    xAxes: [
      {
        ticks: { fontColor: '#64748b', fontSize: 11 },
        gridLines: { display: false },
        barPercentage: 0.7,
      },
    ],
  },
};

const OPCOES_BARRA_HORIZONTAL = {
  ...OPCOES_BASE,
  scales: {
    xAxes: [
      {
        ticks: { beginAtZero: true, precision: 0, fontColor: '#64748b', fontSize: 11 },
        gridLines: { color: 'rgba(148, 163, 184, 0.15)', drawBorder: false },
      },
    ],
    yAxes: [
      {
        ticks: { fontColor: '#64748b', fontSize: 11 },
        gridLines: { display: false },
        barPercentage: 0.65,
      },
    ],
  },
};

const OPCOES_LINHA = {
  ...OPCOES_BASE,
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, precision: 0, fontColor: '#64748b', fontSize: 11 },
        gridLines: { color: 'rgba(148, 163, 184, 0.15)', drawBorder: false },
      },
    ],
    xAxes: [
      {
        ticks: { fontColor: '#64748b', fontSize: 11 },
        gridLines: { color: 'rgba(226, 232, 240, 0.8)', drawBorder: false },
      },
    ],
  },
};

const OPCOES_ROSCA = {
  ...OPCOES_BASE,
  cutoutPercentage: 68,
  legend: {
    ...OPCOES_BASE.legend,
    position: 'right',
  },
};

function calcularKpis(ordens) {
  const agora = moment();
  const abertas = ordens.filter(o => !['FINALIZADA', 'CANCELADA'].includes(o.status));
  const finalizadas = ordens.filter(o => o.status === 'FINALIZADA');
  const finalizadas30d = finalizadas.filter(o => o.data_ultima_atualizacao && moment(o.data_ultima_atualizacao).isAfter(moment().subtract(30, 'days')));
  const vencidas = ordens.filter(o => {
    if (!o.data_prazo || o.status === 'FINALIZADA' || o.status === 'CANCELADA') return false;
    return moment(o.data_prazo).isBefore(agora);
  });
  const taxaConclusao = ordens.length ? Math.round((finalizadas.length / ordens.length) * 100) : 0;
  const tempoMedioHoras = calcularTempoMedioResolucao(finalizadas);

  return {
    total: ordens.length,
    abertas: abertas.length,
    finalizadas: finalizadas.length,
    finalizadas30d: finalizadas30d.length,
    vencidas: vencidas.length,
    taxaConclusao,
    tempoMedioHoras,
  };
}

function calcularTempoMedioResolucao(finalizadas) {
  const duracoes = finalizadas
    .filter(o => o.data_abertura && o.data_ultima_atualizacao)
    .map(o => moment(o.data_ultima_atualizacao).diff(moment(o.data_abertura), 'hours', true))
    .filter(h => h >= 0);

  if (!duracoes.length) return null;
  return Math.round(duracoes.reduce((a, b) => a + b, 0) / duracoes.length);
}

function dadosStatus(ordens) {
  const contagem = STATUS_ORDEM.reduce((acc, s) => {
    acc[s] = 0;
    return acc;
  }, {});

  ordens.forEach(o => {
    if ((contagem[o.status] !== null && contagem[o.status] !== undefined)) contagem[o.status] += 1;
  });

  const ativos = STATUS_ORDEM.filter(s => contagem[s] > 0);
  return {
    labels: ativos.map(s => STATUS_LABELS[s]),
    datasets: [
      {
        data: ativos.map(s => contagem[s]),
        backgroundColor: ativos.map(s => `${STATUS_CORES[s]}cc`),
        borderColor: ativos.map(s => STATUS_CORES[s]),
        borderWidth: 2,
      },
    ],
  };
}

function dadosSla(ordens) {
  const agora = moment();
  let noPrazo = 0;
  let vencido = 0;
  let semPrazo = 0;

  ordens.forEach(o => {
    if (o.status === 'FINALIZADA' || o.status === 'CANCELADA') return;
    if (!o.data_prazo) {
      semPrazo += 1;
      return;
    }
    if (moment(o.data_prazo).isBefore(agora)) vencido += 1;
    else noPrazo += 1;
  });

  const labels = [];
  const data = [];
  const cores = [];
  const bordas = [];

  if (noPrazo) {
    labels.push('No prazo');
    data.push(noPrazo);
    cores.push('#27ae60cc');
    bordas.push('#27ae60');
  }
  if (vencido) {
    labels.push('SLA vencido');
    data.push(vencido);
    cores.push('#e74c3ccc');
    bordas.push('#e74c3c');
  }
  if (semPrazo) {
    labels.push('Sem prazo');
    data.push(semPrazo);
    cores.push('#94a3b8cc');
    bordas.push('#94a3b8');
  }

  return {
    labels,
    datasets: [{ data, backgroundColor: cores, borderColor: bordas, borderWidth: 2 }],
  };
}

function dadosEvolucaoMensal(ordens) {
  const meses = [];
  for (let i = 5; i >= 0; i -= 1) {
    meses.push(moment().subtract(i, 'months').format('YYYY-MM'));
  }

  const abertas = meses.map(() => 0);
  const finalizadas = meses.map(() => 0);

  ordens.forEach(o => {
    if (o.data_abertura) {
      const mes = moment(o.data_abertura).format('YYYY-MM');
      const idx = meses.indexOf(mes);
      if (idx >= 0) abertas[idx] += 1;
    }
    if (o.status === 'FINALIZADA' && o.data_ultima_atualizacao) {
      const mes = moment(o.data_ultima_atualizacao).format('YYYY-MM');
      const idx = meses.indexOf(mes);
      if (idx >= 0) finalizadas[idx] += 1;
    }
  });

  return {
    labels: meses.map(m => moment(m, 'YYYY-MM').format('MMM/YY')),
    datasets: [
      {
        label: 'Abertas',
        data: abertas,
        borderColor: '#3c8dbc',
        backgroundColor: 'rgba(60, 141, 188, 0.08)',
        pointBackgroundColor: '#3c8dbc',
        pointRadius: 4,
        lineTension: 0.3,
        fill: true,
      },
      {
        label: 'Finalizadas',
        data: finalizadas,
        borderColor: '#27ae60',
        backgroundColor: 'rgba(39, 174, 96, 0.06)',
        pointBackgroundColor: '#27ae60',
        pointRadius: 4,
        lineTension: 0.3,
        fill: true,
      },
    ],
  };
}

function dadosPorEquipe(ordens) {
  const mapa = {};
  ordens.forEach(o => {
    const nome = o.equipe_nome || 'Sem equipe';
    mapa[nome] = (mapa[nome] || 0) + 1;
  });

  const entradas = Object.entries(mapa)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return {
    labels: entradas.map(([nome]) => nome),
    datasets: [
      {
        label: 'OS',
        data: entradas.map(([, qtd]) => qtd),
        backgroundColor: 'rgba(60, 141, 188, 0.75)',
        borderColor: '#3c8dbc',
        borderWidth: 1,
      },
    ],
  };
}

function dadosPorTipo(ordens) {
  const mapa = {};
  ordens.forEach(o => {
    const tipo = o.tipo || 'OUTRO';
    mapa[tipo] = (mapa[tipo] || 0) + 1;
  });

  const entradas = Object.entries(mapa).sort((a, b) => b[1] - a[1]);

  return {
    labels: entradas.map(([tipo]) => TIPO_LABELS[tipo] || tipo),
    datasets: [
      {
        label: 'OS',
        data: entradas.map(([, qtd]) => qtd),
        backgroundColor: entradas.map((_, i) => `${TIPO_CORES[i % TIPO_CORES.length]}cc`),
        borderColor: entradas.map((_, i) => TIPO_CORES[i % TIPO_CORES.length]),
        borderWidth: 1,
      },
    ],
  };
}

function dadosPrioridade(ordens) {
  const prioridades = ['CRITICA', 'ALTA', 'MEDIA', 'BAIXA'];
  const labels = { CRITICA: 'Crítica', ALTA: 'Alta', MEDIA: 'Média', BAIXA: 'Baixa' };
  const cores = { CRITICA: '#e74c3c', ALTA: '#f39c12', MEDIA: '#3c8dbc', BAIXA: '#94a3b8' };

  const contagem = prioridades.reduce((acc, p) => {
    acc[p] = 0;
    return acc;
  }, {});

  ordens
    .filter(o => !['FINALIZADA', 'CANCELADA'].includes(o.status))
    .forEach(o => {
      const p = o.prioridade || 'MEDIA';
      if ((contagem[p] !== null && contagem[p] !== undefined)) contagem[p] += 1;
    });

  const ativos = prioridades.filter(p => contagem[p] > 0);

  return {
    labels: ativos.map(p => labels[p]),
    datasets: [
      {
        label: 'Em aberto',
        data: ativos.map(p => contagem[p]),
        backgroundColor: ativos.map(p => `${cores[p]}cc`),
        borderColor: ativos.map(p => cores[p]),
        borderWidth: 1,
      },
    ],
  };
}

function KpiCard({ label, valor, detalhe, cor, icone }) {
  return (
    <div className="os-graficos-kpi-card">
      <div className="os-graficos-kpi-icon" style={{ color: cor }}>
        <i className={`fa fa-${icone}`} />
      </div>
      <div className="os-graficos-kpi-body">
        <span className="os-graficos-kpi-label">{label}</span>
        <strong className="os-graficos-kpi-valor" style={{ color: cor }}>
          {valor}
        </strong>
        {detalhe && <small className="os-graficos-kpi-detalhe">{detalhe}</small>}
      </div>
    </div>
  );
}

function ChartCard({ titulo, subtitulo, children, wide }) {
  return (
    <div className={`os-graficos-chart-card${wide ? ' os-graficos-chart-card--wide' : ''}`}>
      <div className="os-graficos-chart-card-header">
        <h5>{titulo}</h5>
        {subtitulo && <p>{subtitulo}</p>}
      </div>
      <div className="os-graficos-chart-canvas">{children}</div>
    </div>
  );
}

class OrdemServicoGraficos extends Component {
  constructor(props) {
    super(props);
    this.state = { periodo: '90', equipeId: '' };
  }

  renderEmpty() {
    return (
      <div className="os-graficos-empty">
        <i className="fa fa-chart-pie" />
        <span>Nenhuma ordem de serviço no período selecionado.</span>
      </div>
    );
  }

  render() {
    const { periodo, equipeId } = this.state;
    const ordens = filtrarOrdensRelatorio(this.props.list || [], { periodo, equipeId });
    const kpis = calcularKpis(ordens);
    const periodoLabel = (PERIODOS.find(p => p.value === periodo) || {}).label || '';
    const equipes = (this.props.opcoes && this.props.opcoes.equipes) || [];

    const chartStatus = dadosStatus(ordens);
    const chartSla = dadosSla(ordens);
    const chartEvolucao = dadosEvolucaoMensal(this.props.list || []);
    const chartEquipe = dadosPorEquipe(ordens);
    const chartCruzamento = dadosGraficoCruzamento(ordens);
    const chartTipo = dadosPorTipo(ordens);
    const chartPrioridade = dadosPrioridade(ordens);

    const semDadosSla = !chartSla.labels.length;
    const tempoMedioLabel = (kpis.tempoMedioHoras !== null && kpis.tempoMedioHoras !== undefined) ? `${kpis.tempoMedioHoras}h` : '—';

    return (
      <div className="simple-cad-form-premium os-graficos-premium" style={{ marginTop: 20 }}>
        <div className="simple-cad-premium-container">
          <div className="simple-cad-premium-card">
            <div className="simple-cad-premium-header simple-cad-premium-header--actions">
              <div>
                <h4>
                  <i className="fa fa-chart-pie" /> Análise Visual
                </h4>
                <p>Indicadores e tendências das ordens de serviço — período: {periodoLabel}.</p>
              </div>
              <div className="os-graficos-periodo-toolbar">
                <div className="os-graficos-toolbar-field">
                  <label htmlFor="os-graficos-periodo">Período</label>
                  <select
                    id="os-graficos-periodo"
                    className="form-control"
                    value={periodo}
                    onChange={e => this.setState({ periodo: e.target.value })}
                  >
                    {PERIODOS.map(p => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="os-graficos-toolbar-field">
                  <label htmlFor="os-graficos-equipe">Equipe</label>
                  <select
                    id="os-graficos-equipe"
                    className="form-control"
                    value={equipeId}
                    onChange={e => this.setState({ equipeId: e.target.value })}
                  >
                    <option value="">Todas</option>
                    {equipes.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="simple-cad-premium-body">
              <div className="os-graficos-kpi-grid">
                <KpiCard label="Total no período" valor={kpis.total} cor="#2d3748" icone="clipboard-list" />
                <KpiCard label="Em aberto" valor={kpis.abertas} cor="#3c8dbc" icone="clock" />
                <KpiCard
                  label="Taxa de conclusão"
                  valor={`${kpis.taxaConclusao}%`}
                  detalhe={`${kpis.finalizadas} finalizadas`}
                  cor="#27ae60"
                  icone="check-circle"
                />
                <KpiCard
                  label="SLA vencido"
                  valor={kpis.vencidas}
                  cor={kpis.vencidas > 0 ? '#e74c3c' : '#27ae60'}
                  icone="exclamation-triangle"
                />
                <KpiCard
                  label="Finalizadas (30d)"
                  valor={kpis.finalizadas30d}
                  cor="#16a085"
                  icone="flag-checkered"
                />
                <KpiCard
                  label="Tempo médio resolução"
                  valor={tempoMedioLabel}
                  detalhe="OS finalizadas"
                  cor="#8e44ad"
                  icone="hourglass-half"
                />
              </div>

              {ordens.length === 0 ? (
                this.renderEmpty()
              ) : (
                <div className="os-graficos-chart-grid">
                  <ChartCard titulo="Distribuição por status" subtitulo="Composição do backlog e histórico">
                    <Doughnut data={chartStatus} options={OPCOES_ROSCA} />
                  </ChartCard>

                  <ChartCard titulo="Saúde do SLA" subtitulo="OS ativas com prazo definido">
                    {semDadosSla ? (
                      <div className="os-graficos-chart-placeholder">Nenhuma OS ativa com prazo no período.</div>
                    ) : (
                      <Doughnut data={chartSla} options={OPCOES_ROSCA} />
                    )}
                  </ChartCard>

                  <ChartCard
                    titulo="Evolução mensal"
                    subtitulo="Aberturas vs finalizações — últimos 6 meses"
                    wide
                  >
                    <Line data={chartEvolucao} options={OPCOES_LINHA} />
                  </ChartCard>

                  <ChartCard titulo="OS por equipe" subtitulo="Top 8 equipes no período">
                    <HorizontalBar data={chartEquipe} options={OPCOES_BARRA_HORIZONTAL} />
                  </ChartCard>

                  <ChartCard titulo="OS por cruzamento" subtitulo="Top 8 cruzamentos no período">
                    <HorizontalBar data={chartCruzamento} options={OPCOES_BARRA_HORIZONTAL} />
                  </ChartCard>

                  <ChartCard titulo="OS por tipo" subtitulo="Corretiva, preventiva e demais">
                    <Bar data={chartTipo} options={OPCOES_BARRA} />
                  </ChartCard>

                  <ChartCard titulo="Prioridade em aberto" subtitulo="Carga operacional por urgência">
                    <Bar data={chartPrioridade} options={OPCOES_BARRA} />
                  </ChartCard>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  list: state.manutencaoOS.list,
  opcoes: state.manutencaoOS.opcoes,
});

export default connect(mapStateToProps)(OrdemServicoGraficos);
